// IconButton (audit §4b): a glyph alone, 34 × 34 (DESIGN.md §8), named for screen readers by its label.
// Given isSelected it is a toggle, and on is filled in ink (the row switch, a map layer). Hover doubles
// the frame, as a glyph can't be underlined. Reads only --iconbtn-*.
// The board's corner (concept A, 26 Oct) adds:
// - filled: in ink though not a toggle (Filter while filters are on).
// - count: a number over its top end corner (Filter's "2"); its label says it ("Filter, 2 on").
// - tip: its name on hover, after HoverCard's pause, and at once on keyboard focus, in the pop layer's
//   frame, with a muted second line when it has one ("Right-click or ▾ for options").
// - off: it can't be used, and why: muted, still reachable, so the tip says the reason; a press does nothing.
// - caret and join: a narrow ▾ joined to the button before it, so the two draw as one frame (Group ▾).
// - menu: it opens a menu (aria-haspopup, aria-expanded, aria-controls kept right by the caller).
// The corner's option A (Amir, 26 Oct; pages/board-corner) adds two opt-in props:
// - size="sm": §8's small control, 26 × 26; a count is a word inside it, after the glyph ("⏷ 2"), not
//   boxed over its corner. At touch widths the drawing stays 26 and its press area is 44 × 44 (an
//   invisible ring round it, laid out by its join so a pair's rings never overlap).
// - quiet: no frame at rest; a hairline ring on hover (Close's), an ink ring while pressed or while its
//   menu is open; filled in ink when on or filled, as before. A quiet ▾ joined to a button that's on gets
//   an ink edge, so the pair reads as one split button.
import type { ReactElement } from 'react';
import {
  Button as AriaButton,
  Focusable,
  ToggleButton,
  Tooltip,
  TooltipTrigger,
} from 'react-aria-components';
import { Glyph, type GlyphName } from '../Glyph/Glyph.tsx';
import { HOVER_CLOSE_MS, HOVER_OPEN_MS } from '../HoverCard/HoverCard.tsx';
import { LayerFrame } from '../Layer/Layer.tsx';
import { tokenPx } from '../token-value.ts';
import './IconButton.css';

export interface IconButtonTip {
  /** Its name ("Group by division"). */
  title: string;
  /** A muted second line: how else to use it, or why it's off. */
  note?: string;
}

export interface IconButtonProps {
  /** Any glyph but the ×: closing is Close's alone, which draws no frame (round 2, Amir 26 Oct). */
  icon: Exclude<GlyphName, 'close'>;
  /** What it does, for screen readers ("Previous day"); with a count, the count too ("Filter, 2 on"). */
  label: string;
  onPress?: () => void;
  isDisabled?: boolean;
  /** A toggle when given: on is filled in ink. */
  isSelected?: boolean;
  onChange?: (isSelected: boolean) => void;
  /** Filled in ink without being a toggle: something it opens is set (Filter with filters on). */
  filled?: boolean;
  /** A number over its corner, drawn only above zero. */
  count?: number;
  /** Shown on hover and keyboard focus. */
  tip?: string | IconButtonTip;
  /** It can't be used, and why: muted but reachable, the reason in its tip. */
  off?: boolean;
  /** The narrow ▾ beside another button. */
  caret?: boolean;
  /** Joined to its neighbour: 'end' has its end edge square (the first of two), 'start' its start edge. */
  join?: 'start' | 'end';
  /** It opens a menu: whether it's open, and the menu's id while it is. */
  menu?: { expanded: boolean; controls?: string };
  /** For stories and tests: the tip held open. */
  tipOpen?: boolean;
  /** §8's small control (26 px), its count a word inside it; 44 px to press at touch widths. */
  size?: 'sm';
  /** No frame until it's on: a hairline ring on hover, an ink ring pressed or open. */
  quiet?: boolean;
}

export function IconButton({
  icon,
  label,
  onPress,
  isDisabled,
  isSelected,
  onChange,
  filled,
  count,
  tip,
  off,
  caret,
  join,
  menu,
  tipOpen,
  size,
  quiet,
}: IconButtonProps) {
  const glyph = <Glyph name={icon} className="fl-iconbtn__glyph" />;
  const badge =
    count && count > 0 ? (
      <span className="fl-iconbtn__count" aria-hidden="true">
        {count}
      </span>
    ) : null;
  const data = {
    'data-caret': caret ? '' : undefined,
    'data-join': join,
    'data-off': off ? '' : undefined,
    'data-filled': filled ? '' : undefined,
    'data-size': size,
    'data-quiet': quiet ? '' : undefined,
  };
  const menuProps = menu
    ? {
        'aria-haspopup': 'menu' as const,
        'aria-expanded': menu.expanded,
        ...(menu.controls ? { 'aria-controls': menu.controls } : {}),
      }
    : {};
  // off: reachable, so its reason is read and shown; nothing happens on a press
  const offButton = (
    <button type="button" className="fl-iconbtn" aria-label={label} aria-disabled="true" {...data}>
      {glyph}
      {badge}
    </button>
  );
  let button: ReactElement;
  if (off) button = offButton;
  else if (isSelected !== undefined)
    button = (
      <ToggleButton
        className="fl-iconbtn"
        aria-label={label}
        isSelected={isSelected}
        onChange={onChange}
        isDisabled={isDisabled}
        {...data}
      >
        {glyph}
        {badge}
      </ToggleButton>
    );
  else
    button = (
      <AriaButton
        className="fl-iconbtn"
        aria-label={label}
        onPress={onPress}
        isDisabled={isDisabled}
        {...menuProps}
        {...data}
      >
        {glyph}
        {badge}
      </AriaButton>
    );
  if (!tip) return button;
  const t = typeof tip === 'string' ? { title: tip } : tip;
  return (
    <TooltipTrigger delay={HOVER_OPEN_MS} closeDelay={HOVER_CLOSE_MS} isOpen={tipOpen}>
      {off ? <Focusable>{offButton}</Focusable> : button}
      <Tooltip className="fl-iconbtn__tip" placement="bottom start" offset={tokenPx('--iconbtn-tip-offset')}>
        <LayerFrame level="pop" label={t.title}>
          <span className="fl-iconbtn__tip-title">{t.title}</span>
          {t.note ? <span className="fl-iconbtn__tip-note">{t.note}</span> : null}
        </LayerFrame>
      </Tooltip>
    </TooltipTrigger>
  );
}
